import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  RefreshCw,
  BarChart3,
  FileText,
  IndianRupee,
  Plus,
  BookOpen,
  Calculator,
  PieChart,
  Receipt,
  Settings,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';

const FinanceDashboard = ({ user, isTab, tabId, tabData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const dashboardRes = await api.get('/finance/dashboard').catch(() => ({ data: null }));
      
      setDashboard(dashboardRes.data);
      
      // Check if setup is needed
      if (!dashboardRes.data?.financial_year) {
        setNeedsSetup(true);
      } else {
        setNeedsSetup(false);
      }
    } catch (error) {
      console.error('Error fetching finance data:', error);
      setNeedsSetup(true);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Show message to complete setup in Admin
  if (needsSetup) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto mt-12">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Finance Module Not Configured</h2>
              <p className="text-gray-600 mb-6">
                Please complete the finance setup from the Admin section to access the Finance Dashboard.
              </p>
              <Button 
                onClick={() => navigate('/admin/company/finance-setup')}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Settings className="h-4 w-4 mr-2" />
                Go to Finance Setup
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Dashboard with Finance Trends
  const summary = dashboard?.summary || {};
  const voucherStats = dashboard?.voucher_stats || {};

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finance Dashboard</h1>
          <p className="text-gray-600 text-sm">
            {dashboard?.financial_year?.name || 'Current Financial Year'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/finance/journal')}>
            <Plus className="h-4 w-4 mr-1" />
            New Entry
          </Button>
          <Button size="sm" onClick={() => navigate('/admin/finance/reports')}>
            <BarChart3 className="h-4 w-4 mr-1" />
            Reports
          </Button>
        </div>
      </div>

      {/* Revenue & Expense Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Total Revenue</p>
                <p className="text-xl font-bold text-green-800 mt-1">
                  {formatCurrency(summary.total_income)}
                </p>
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  This financial year
                </p>
              </div>
              <div className="h-10 w-10 bg-green-200 rounded-full flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-red-700 uppercase tracking-wide">Total Expenditure</p>
                <p className="text-xl font-bold text-red-800 mt-1">
                  {formatCurrency(summary.total_expenses)}
                </p>
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3" />
                  This financial year
                </p>
              </div>
              <div className="h-10 w-10 bg-red-200 rounded-full flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-red-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${summary.net_profit >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wide ${summary.net_profit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                  Net {summary.net_profit >= 0 ? 'Profit' : 'Loss'}
                </p>
                <p className={`text-xl font-bold mt-1 ${summary.net_profit >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>
                  {formatCurrency(Math.abs(summary.net_profit || 0))}
                </p>
                <p className={`text-xs mt-1 ${summary.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                  Revenue - Expenses
                </p>
              </div>
              <div className={`h-10 w-10 ${summary.net_profit >= 0 ? 'bg-blue-200' : 'bg-orange-200'} rounded-full flex items-center justify-center`}>
                <IndianRupee className={`h-5 w-5 ${summary.net_profit >= 0 ? 'text-blue-700' : 'text-orange-700'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">Cash Balance</p>
                <p className="text-xl font-bold text-purple-800 mt-1">
                  {formatCurrency(summary.cash_balance)}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Current balance
                </p>
              </div>
              <div className="h-10 w-10 bg-purple-200 rounded-full flex items-center justify-center">
                <Wallet className="h-5 w-5 text-purple-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Receivables & Payables */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Accounts Receivable</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(summary.total_receivables)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Amount to be collected</p>
              </div>
              <ArrowUpRight className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Accounts Payable</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(summary.total_payables)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Amount to be paid</p>
              </div>
              <ArrowDownRight className="h-6 w-6 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Journal Entries</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-bold text-gray-900">{voucherStats.total || 0}</span>
                  <Badge variant="outline" className="text-green-600 border-green-200 text-xs">
                    {voucherStats.posted || 0} posted
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 mt-1">Total transactions</p>
              </div>
              <FileText className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Recent Entries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center justify-center gap-1.5"
                onClick={() => navigate('/admin/finance/ledgers')}
              >
                <BookOpen className="h-4 w-4 text-blue-600" />
                <span className="text-xs">Ledgers</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center justify-center gap-1.5"
                onClick={() => navigate('/admin/finance/journal')}
              >
                <FileText className="h-4 w-4 text-green-600" />
                <span className="text-xs">Journal</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center justify-center gap-1.5"
                onClick={() => navigate('/admin/finance/reports/trial-balance')}
              >
                <Calculator className="h-4 w-4 text-purple-600" />
                <span className="text-xs">Trial Balance</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center justify-center gap-1.5"
                onClick={() => navigate('/admin/finance/reports/profit-loss')}
              >
                <PieChart className="h-4 w-4 text-orange-600" />
                <span className="text-xs">P&L Statement</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center justify-center gap-1.5"
                onClick={() => navigate('/admin/finance/gst')}
              >
                <Receipt className="h-4 w-4 text-teal-600" />
                <span className="text-xs">GST Reports</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center justify-center gap-1.5"
                onClick={() => navigate('/admin/finance/reports/balance-sheet')}
              >
                <BarChart3 className="h-4 w-4 text-indigo-600" />
                <span className="text-xs">Balance Sheet</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Entries */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Recent Journal Entries</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/finance/journal')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_entries?.length > 0 ? (
              <div className="space-y-2">
                {dashboard.recent_entries.slice(0, 4).map((entry, idx) => (
                  <div 
                    key={entry.id || idx} 
                    className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                    onClick={() => navigate(`/admin/finance/journal/${entry.id}`)}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                        entry.voucher_type === 'sales' ? 'bg-green-100' :
                        entry.voucher_type === 'purchase' ? 'bg-red-100' :
                        entry.voucher_type === 'receipt' ? 'bg-blue-100' :
                        entry.voucher_type === 'payment' ? 'bg-orange-100' :
                        'bg-gray-100'
                      }`}>
                        <FileText className={`h-4 w-4 ${
                          entry.voucher_type === 'sales' ? 'text-green-600' :
                          entry.voucher_type === 'purchase' ? 'text-red-600' :
                          entry.voucher_type === 'receipt' ? 'text-blue-600' :
                          entry.voucher_type === 'payment' ? 'text-orange-600' :
                          'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-xs">{entry.voucher_number}</p>
                        <p className="text-[10px] text-gray-500 truncate max-w-[150px]">{entry.narration}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-xs">{formatCurrency(entry.total_debit)}</p>
                      <Badge 
                        variant="outline" 
                        className={`text-[10px] ${
                          entry.status === 'posted' ? 'text-green-600 border-green-200' :
                          'text-yellow-600 border-yellow-200'
                        }`}
                      >
                        {entry.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No journal entries yet</p>
                <Button 
                  variant="link" 
                  className="mt-1 text-sm"
                  onClick={() => navigate('/admin/finance/journal/new')}
                >
                  Create your first entry
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinanceDashboard;
